/**
 * The two cards under the punch button: this week, and the last few days.
 *
 * Asked for with the October 2026 redesign, from the owner's reference screenshot: "add
 * attendance like in ss" — a short log of recent days with their in, out, total and status, and
 * a way to the full month. Both are read from endpoints the app already calls on the Hours and
 * Weekly Off tabs; nothing new is asked of the server, and nothing here can change anything.
 *
 * They are supplementary. If either request fails — no signal at the gate, a server restarting —
 * the card simply does not appear, and the punch button above it is untouched.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays } from 'lucide-react';
import { fmtDayMonth, fmtTime, fmtWeekday, isBelowTarget } from '@siva/shared';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { shiftLabel } from '../lib/ui.js';
import { t } from '../i18n/index.js';

/** One day as /me/hours describes it — only the fields these cards read. */
type Day = {
  date: string;
  status: 'present' | 'present_no_punchout' | 'absent' | 'weekly_off' | 'holiday';
  first_in: string | null;
  last_out: string | null;
  worked_minutes: number;
  shortfall_minutes: number;
  worked_on_off: boolean;
  live: boolean;
};
type Hours = {
  month: string;
  today: string;
  week: { days: Day[] };
  month_rows: { days: Day[] };
};
type Week = { week_start: string; published: boolean; off_date: string | null };

/** How many finished days the log shows; the rest are a tap away on Hours. */
const LOG_DAYS = 5;

const hm = (min: number) => `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, '0')}m`;

const prevMonth = (ym: string) => {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
};

/**
 * What a finished day was, in the words the Hours tab already uses, and the colour it is shown in.
 *
 * Below target follows the same half-hour grace as every report (isBelowTarget), so a day eight
 * minutes short reads Present here exactly as it does on the office's daily report.
 */
function statusOf(d: Day): { label: string; tone: string } {
  switch (d.status) {
    case 'present':
      return isBelowTarget(d.shortfall_minutes)
        ? { label: t('today_below_target'), tone: 'amber' }
        : { label: t('hours_st_present'), tone: 'green' };
    case 'present_no_punchout':
      return { label: t('hours_st_no_punchout'), tone: 'violet' };
    case 'absent':
      return { label: t('hours_st_absent'), tone: 'red' };
    case 'holiday':
      return { label: t('hours_st_holiday'), tone: 'blue' };
    case 'weekly_off':
      return d.worked_on_off
        ? { label: t('hours_st_worked_on_off'), tone: 'green' }
        : { label: t('hours_st_off'), tone: 'neutral' };
  }
}

export function HomeWeek() {
  const { me } = useAuth();
  const [week, setWeek] = useState<Week | null>(null);
  const [today, setToday] = useState<string | null>(null);
  const [log, setLog] = useState<Day[] | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const r = await api<{ weeks: Week[] }>('/me/off-days');
        if (alive) setWeek(r.weeks[0] ?? null);
      } catch {
        /* the card is supplementary: no week, no card */
      }
    })();
    void (async () => {
      try {
        const h = await api<Hours>('/me/hours');
        // The days that have finished, newest first. A month only a few days old does not hold
        // five of them, so the month before is asked for once, and only then.
        const finished = (days: Day[]) => days.filter((d) => d.date < h.today && !d.live);
        const seen = new Map<string, Day>();
        for (const d of [...finished(h.month_rows.days), ...finished(h.week.days)]) seen.set(d.date, d);
        if (seen.size < LOG_DAYS) {
          const p = await api<Hours>(`/me/hours?month=${prevMonth(h.month)}`);
          for (const d of finished(p.month_rows.days)) if (!seen.has(d.date)) seen.set(d.date, d);
        }
        const rows = [...seen.values()]
          .sort((a, b) => b.date.localeCompare(a.date))
          .slice(0, LOG_DAYS);
        if (alive) {
          setToday(h.today);
          setLog(rows);
        }
      } catch {
        /* the card is supplementary: no log, no card */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const shift = me?.user?.shift;
  const offToday = !!week?.off_date && week.off_date === today;

  return (
    <>
      {week && today && (
        <section className="card home-week" aria-labelledby="home-week-title">
          <div className="home-week-head">
            <span className="home-week-icon" aria-hidden="true">
              <CalendarDays size={20} />
            </span>
            <div className="home-week-title">
              <h3 id="home-week-title">{t('home_this_week')}</h3>
            </div>
            <Link to="/off-days" className="home-link">
              {t('home_see_all')}
            </Link>
          </div>
          <div className="home-day is-today">
            <span className="home-day-tile">
              <strong>{Number(today.slice(8, 10))}</strong>
              <span>{fmtWeekday(today).slice(0, 3)}</span>
            </span>
            <span className="home-day-body">
              <strong>{offToday ? t('hours_st_off') : shift ? shiftLabel(shift) : '—'}</strong>
            </span>
            <span className="home-tag">{t('home_today')}</span>
          </div>
          {!week.published ? (
            <p className="muted home-note">{t('home_off_not_published')}</p>
          ) : week.off_date && !offToday ? (
            <div className="home-day">
              <span className="home-day-tile">
                <strong>{Number(week.off_date.slice(8, 10))}</strong>
                <span>{fmtWeekday(week.off_date).slice(0, 3)}</span>
              </span>
              <span className="home-day-body">
                <strong>{t('hours_st_off')}</strong>
                <span className="muted">{fmtDayMonth(week.off_date)}</span>
              </span>
              <span className="home-tag neutral">{t('home_off')}</span>
            </div>
          ) : null}
        </section>
      )}

      {log && log.length > 0 && (
        <section className="home-log" aria-labelledby="home-log-title">
          <div className="home-log-head">
            <h3 id="home-log-title">{t('home_attendance_log')}</h3>
            <Link to="/hours" className="home-link">
              {t('home_view_all')}
            </Link>
          </div>
          <div className="card home-log-card">
            <table className="home-log-table">
              <thead>
                <tr>
                  <th scope="col">{t('home_log_date')}</th>
                  <th scope="col">{t('home_log_in')}</th>
                  <th scope="col">{t('home_log_out')}</th>
                  <th scope="col">{t('home_log_total')}</th>
                  <th scope="col" className="num">
                    {t('home_log_status')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {log.map((d) => {
                  const s = statusOf(d);
                  return (
                    <tr key={d.date}>
                      <th scope="row">{fmtDayMonth(d.date)}</th>
                      <td>{d.first_in ? fmtTime(d.first_in) : '—'}</td>
                      <td>{d.last_out ? fmtTime(d.last_out) : '—'}</td>
                      <td className="total">{d.worked_minutes > 0 ? hm(d.worked_minutes) : '—'}</td>
                      <td className={`num status tone-${s.tone}`}>{s.label}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}
